<template>
  <content-subpage
    class="add-teacher-from-group-content-subpage"
    :visible="visible"
    :titles="titles"
    :has-parent-subpage="hasParentSubpage"
    :back-label="backLabel"
    @content-subpage-hidden="actionHidden(params.actionHidden)"
    @content-subpage-back="actionBack()"
  >
    <template #main-content>
      <dropdown-component
        class="class-group-dropdown"
        kind="white"
        :value="selectedClassGroup"
        @change="handleChangeClassGroupSelected"
      >
        <cv-dropdown-item
          v-for="(classGroup, index) in availableClassGroups"
          :key="index"
          :value="classGroup.groupName"
        >
          {{ classGroup.groupName }}
        </cv-dropdown-item>
      </dropdown-component>

      <div class="add-teacher-selection-listing">
        <search-bar
          auto-collapse
          :keyword="teacherName"
          active
          filter-type="selection"
          placeholder="Find Teachers"
          @search="handleSearch"
          @settings-click="toggleSettings"
        >
          <template #filter="{ filterChanged }">
            <filter-selection
              :filter-count="noOfActiveFilters"
              @click="toggleFilter"
              @remove="removeFilters(filterChanged)"
            />
            <add-teacher-from-group-filter
              :visible="showFilterPopup"
              :roles="roles"
              :schools="schools"
              :filter-flags="params.filterFlags"
              :applied-search-filters="appliedSearchFilters"
              @change="filterChanged"
              @close="closeFilterContentPopup"
            />
          </template>
        </search-bar>
        <filter-tag-list v-if="filterTags.length > 0">
          <filter-tag
            v-for="(tag, index) in filterTags"
            :key="index"
            :label="tag.label"
            :count="tag.count"
            @remove="handleRemoveTag(tag)"
          />
        </filter-tag-list>

        <data-table-list in-subpage>
          <template #selection-bar>
            <selection-bar :count="rowsSelected" @clear="handleClearSelection" />
          </template>
          <!--FIXME VUE3: CURRENTLY NOT SUPPORTED
                :showFirstLastPage="footerProps.showFirstLastPage" FIXED
                :mobile-breakpoint="0"
          -->
          <v-data-table
            v-model:page="options.page"
            class="_items-selectable show-first-last"
            v-model:sort-by="options.sortBy"
            :headers="listingHeaders"
            v-model="selectedInner"
            :items-per-page-options="footerProps.itemsPerPageOptions"
            :show-current-page="footerProps.showCurrentPage"
            :items-per-page-text="footerProps.itemsPerPageText"
            :items="filteredTeacherList"
            item-value="userId"
            fixed-header
            :loading="isDataTableLoading"
            :mobile-breakpoint="0"
            :items-per-page="options.itemsPerPage"
            show-select
            item-selectable="selectable"
            must-sort
          >
            <template #[`item.name`]="{ item }">
              <data-table-cell class="name" :class="{ active: sortColumn === 'name' }">
                <ellipsis-text :text="item.name" />
              </data-table-cell>
            </template>
            <template #[`item.email`]="{ item }">
              <data-table-cell class="email" :class="{ active: sortColumn === 'email' }">
                <span>{{ item.email }}</span>
              </data-table-cell>
            </template>
            <template #[`item.schoolName`]="{ item }">
              <data-table-cell class="school" :class="{ active: sortColumn === 'schoolName' }">
                <span>{{ item.schoolName }}</span>
              </data-table-cell>
            </template>
            <template #[`item.roleInGroup`]="{ item }">
              <data-table-cell class="role" :class="{ active: sortColumn === 'roleInGroup' }">
                <ellipsis-text class="label" :text="getRoleLabel(item.roleInGroup)" />
              </data-table-cell>
            </template>
          </v-data-table>
        </data-table-list>
      </div>
    </template>
    <template #action-button>
      <cv-button class="add" :disabled="isDisabled" @click="handleAddTeacher">
        <svg-icon name="Plus24" kind="action" />
        <span>{{ t("add_recipient_from_school_button_label") }}</span>
      </cv-button>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import {
  ContentSubpageParams,
  useContentSubpage,
} from "@plugins/content-subpage/composables/content-subpage.ts";
import { isDefined, isEmpty } from "@utils/var.check";
import StudentGroupApi from "@apis/student.group.api";
import {
  ROLE_IN_GROUP_LABEL,
  ROLE_IN_GROUP,
} from "@plugins/student-group/constant/student.group.constant";
import { ADD_TEACHER_FILTER_TAG_LABELS } from "@modules/notification/service/notification.constant";
import AddTeacherFromGroupFilter from "@modules/notification/components/AddTeacherFromGroupFilter";
import ObjectUtils from "@utils/object.utils";
import DatatableUtils from "@plugins/vuetify/service/datatable.utils";

// Props
interface AddTeacherFromGroupParams extends ContentSubpageParams {
  filterFlags?: {
    group: boolean;
    name: boolean;
    email: boolean;
    school: boolean;
    role: boolean;
  };
  excludedUserIds?: (string | number)[]; // Assuming IDs are string or number; refine if needed
  settings?: Record<string, unknown>;
  selectedStudentGroup?: {
    groupName: string;
    uuid: string;
    studentGroupCollectionId: string | number; // Assuming IDs are string or number; change to string if needed
  }[];
  selectedTeachers: unknown[];
}

interface Props {
  params?: AddTeacherFromGroupParams | null;
}

const props = withDefaults(defineProps<Props>(), {
  params: null,
});

// Composables
const { t } = useI18n();
const { passiveApiFailHandler } = useErrorHandler();

const dirtyId = ref(""); // was not overwritten

const {
  titles,
  visible,
  actionHidden,
  actionBack,
  hasParentSubpage,
  isComponentDirty,
  setDirty,
  removeDirty,
  defaultExpose,
} = useContentSubpage(props.params, dirtyId);

// Emits
const emit = defineEmits<(e: "teachersAdded", payload: Record<string, unknown>) => void>();

// Data
const parentSubpageName = ref("");
const showFilterPopup = ref(false);
const appliedSearchFilters = ref<Record<string, unknown>>({});
const showSettingsContentPopup = ref(false);
const selectedClassGroup = ref("");
const teacherName = ref("");
const selectedClassGroupUuid = ref("");
const selectedClassGroupCollectionId = ref("");
const lastSelectedClassGroupUuid = ref("");
const lastSelectedClassGroupCollectionId = ref("");
const selectedAndNewlySelected = ref<unknown[]>([]);
const selectedInner = ref<unknown[]>([]);
const selected = ref<unknown[]>([]);
const initialSelected = ref<unknown[]>([]);
const selectedRecipients = ref<Record<string, unknown>>({});

const listingHeaders = ref<unknown[]>([]);
const footerProps = ref<unknown>({});
const isDataTableLoading = ref(true);
const options = ref<unknown>({});
const teacherList = ref<unknown[]>([]); // Original
const filteredTeacherList = ref<unknown[]>([]);
const teacherListByGroup = ref<Record<string, unknown>>({});
const studentGroupCheckedMap = ref(new Set()); // Used to check if student group has been fetched

// Watchers
watch(
  selectedInner,
  () => {
    updateDirtyData();
    DatatableUtils.filterSelected(
      selectedInner.value,
      filteredTeacherList.value,
      "userId",
      selected.value,
    );
  },
  { deep: true },
);

// Computed
// const showDataTable = computed(() => isEmpty(filteredTeacherList.value) || isEmpty(teacherList.value));

const initialSelectedTeachers = computed(() => props.params?.selectedTeachers ?? {});

//const excludedUserIds = computed(() => props.params?.excludedUserIds ?? []);

const availableClassGroups = computed(() => props.params?.selectedStudentGroup ?? []);

const schools = computed(() => {
  const uniqueSchools = [
    ...new Set(
      teacherList.value.flatMap((teacher) =>
        isEmpty(teacher.schoolName) ? [] : teacher.schoolName,
      ),
    ),
  ];
  return uniqueSchools.map((schoolName) => ({
    value: schoolName,
    label: schoolName,
    name: schoolName,
  }));
});

const roles = computed(() => {
  const uniqueRoles = [
    ...new Set(
      teacherList.value.flatMap((teacher) =>
        isEmpty(teacher.roleInGroup) ? [] : teacher.roleInGroup,
      ),
    ),
  ];
  return uniqueRoles.map((roleInGroup) => ({
    value: roleInGroup,
    label: getRoleLabel(roleInGroup),
    name: roleInGroup,
  }));
});

const backLabel = computed(() => `${t("content_subpage_go_back_link")} ${parentSubpageName.value}`);

//const isFromActivityTeams = computed(() => isDefined(props.params?.availableStudentsList) && !isEmpty(props.params?.settings));

const noOfActiveFilters = computed(() => {
  let count = 0;
  if (isDefined(appliedSearchFilters.value.role) && appliedSearchFilters.value.role.length > 0)
    count++;
  if (isDefined(appliedSearchFilters.value.school) && appliedSearchFilters.value.school.length > 0)
    count++;
  return count;
});

const filterTags = computed(() => {
  const filters = Object.keys(appliedSearchFilters.value);
  if (isDefined(filters) && filters.length > 0) {
    return filters
      .map((searchFilter) => {
        let count = 0;
        if (Array.isArray(appliedSearchFilters.value[searchFilter])) {
          count = appliedSearchFilters.value[searchFilter].length;
        } else if (
          isDefined(appliedSearchFilters.value[searchFilter]) &&
          appliedSearchFilters.value[searchFilter].length
        ) {
          // school filter is not array
          count = 1;
        }
        let label = "";
        if (count === 1) {
          if (searchFilter === "school") {
            label = appliedSearchFilters.value[searchFilter].toString();
          } else {
            label = getRoleLabel(appliedSearchFilters.value[searchFilter]);
          }
        } else {
          label = t(ADD_TEACHER_FILTER_TAG_LABELS[0][searchFilter]);
        }
        return { type: searchFilter, label, count };
      })
      .filter((searchFilter) => searchFilter.count !== 0);
  }
  return [];
});

const sortColumn = computed(() =>
  options.value.sortBy.length > 0 ? options.value.sortBy[0].key : "",
);

const isDisabled = computed(() => !isComponentDirty());

// Methods
const initDataTableOptions = () => {
  const itemsPerPage = window.matchMedia("(max-width: 671px)").matches ? 10 : 20;
  options.value = {
    page: 1,
    itemsPerPage,
    sortBy: [{ key: "name", order: "asc" }],
  };
  footerProps.value = {
    itemsPerPageOptions: [], // Override to hide
    itemsPerPageText: "", // Override to hide
    itemsPerPageAllText: "", // Override to hide
    showFirstLastPage: true,
    showCurrentPage: true,
  };
  listingHeaders.value = [
    { title: t("student_group_detail_header_name"), class: "name", key: "name" },
    { title: t("student_group_detail_header_email"), class: "email", key: "email" },
    { title: t("student_group_detail_header_school"), class: "school", key: "schoolName" },
    { title: t("student_group_detail_header_role"), class: "role", key: "roleInGroup" },
  ];
};

const getRoleLabel = (role: string) => ROLE_IN_GROUP_LABEL[role];

// const settingsContentPopupRefElement = () => {
//   return DOMUtils.elOf(
//     document,
//     ".teacher-selection-listing-from-group-content-subpage .search-bar-wrapper"
//   )[0] ?? null;
// };

const fetchTeachers = (classGroup) => {
  return new Promise((resolve) => {
    if (classGroup.uuid === "") {
      resolve();
      return;
    }
    isDataTableLoading.value = true;

    StudentGroupApi.fetchStudentGroupTeacher(classGroup.uuid).then(
      (response) => {
        const teachers = response.data.filter(
          (teacher) => teacher.roleInGroup !== ROLE_IN_GROUP.LIAISON_TEACHER,
        );
        removeFilters();
        teacherListByGroup.value[classGroup.studentGroupCollectionId] = teachers;
        teacherList.value = teacherList.value.concat(teachers);
        isDataTableLoading.value = false;
        resolve();
      },
      () => {
        passiveApiFailHandler();
        resolve();
      },
    );
  });
};

const handleChangeClassGroupSelected = (value: string) => {
  const [classGroup] = availableClassGroups.value.filter(
    (availableClassGroup) => availableClassGroup.groupName === value,
  );

  lastSelectedClassGroupUuid.value = selectedClassGroupUuid.value;
  lastSelectedClassGroupCollectionId.value = selectedClassGroupCollectionId.value;
  selectedClassGroupUuid.value = classGroup.uuid;
  selectedClassGroupCollectionId.value = classGroup.studentGroupCollectionId;
  selectedClassGroup.value = value;
  selectedAndNewlySelected.value = [...selected.value];
  filteredTeacherList.value = teacherListByGroup.value[selectedClassGroupCollectionId.value] ?? [];
  handleSelectedTeachersInClassGroups();
};

const initData = () => {
  selectedClassGroupUuid.value = availableClassGroups.value[0]?.uuid ?? "";
  selectedClassGroupCollectionId.value =
    availableClassGroups.value[0]?.studentGroupCollectionId ?? "";
  selectedClassGroup.value = availableClassGroups.value[0]?.groupName ?? "";

  const promiseArray = [];
  availableClassGroups.value.forEach((classGroup) => {
    promiseArray.push(fetchTeachers(classGroup));
  });
  Promise.all(promiseArray).then(() => {
    initSelectedTeachers();
    filteredTeacherList.value =
      teacherListByGroup.value[selectedClassGroupCollectionId.value] ?? [];
    handleSelectedTeachersInClassGroups();
  });
};

const initSelectedTeachers = () => {
  selectedInner.value = [];
  if (!isEmpty(initialSelectedTeachers.value)) {
    const recipientInOtherClassGroup = new Set();
    for (const [classGroup, recipients] of Object.entries(initialSelectedTeachers.value)) {
      if (classGroup == selectedClassGroupCollectionId.value) {
        let selectedTeachersInClassGroup = isEmpty(selectedRecipients.value[classGroup])
          ? []
          : selectedRecipients.value[classGroup];
        recipients.teachers?.forEach((userId: string) => {
          const selectedTeacher = teacherList.value
            .filter((recipient) => recipient.userId === userId)
            // FIXME: adding new property cause v-model not able to select
            .map((teacher) => ({
              ...teacher,
              classGroupUuid: selectedClassGroupUuid.value,
              studentGroupCollectionId: parseInt(classGroup),
            }));
          selectedTeachersInClassGroup = selectedTeachersInClassGroup.concat(selectedTeacher);
        });
        selectedRecipients.value[classGroup] = selectedTeachersInClassGroup.filter(
          (recipientA, index: number, self) =>
            index === self.findIndex((recipientB) => recipientB.userId === recipientA.userId),
        );
      } else {
        recipients.teachers?.forEach((userId: string) => recipientInOtherClassGroup.add(userId));
      }
    }

    // TODO: to fix selection issue
    recipientInOtherClassGroup.forEach((userId: string) => {
      const teacher = teacherList.value.find((recipient) => recipient.userId === userId);

      if (!isEmpty(teacher)) {
        const teacherData = {
          ...teacher,
          classGroupUuid: selectedClassGroupUuid.value,
          studentGroupCollectionId: parseInt(selectedClassGroupCollectionId.value),
        };
        if (isDefined(selectedRecipients.value[selectedClassGroupCollectionId.value])) {
          const indexInList = selectedRecipients.value[
            selectedClassGroupCollectionId.value
          ].findIndex((recipient) => recipient.userId === teacher.userId);
          if (indexInList === -1) {
            selectedRecipients.value[selectedClassGroupCollectionId.value].push(teacherData);
          }
        } else {
          selectedRecipients.value[selectedClassGroupCollectionId.value] = [];
          selectedRecipients.value[selectedClassGroupCollectionId.value].push(teacherData);
        }
      }
    });
  }

  if (!isEmpty(selectedRecipients.value) && !isEmpty(selectedClassGroupUuid.value)) {
    let recipients = Object.values(selectedRecipients.value).flat(1);
    recipients = recipients.filter(
      (recipientA, index: number, self) =>
        index === self.findIndex((recipientB) => recipientB.userId === recipientA.userId),
    );
    recipients.forEach((recipient) => {
      teacherList.value.forEach((teacher) => {
        if (recipient.userId === teacher.userId) {
          const selectedIndex = selectedInner.value.findIndex(
            (userId) => userId === recipient.userId,
          );
          if (selectedIndex === -1) selectedInner.value.push(recipient.userId);
        }
      });
    });
    initialSelected.value = selectedInner.value.map((recipient) => recipient);
  }
  studentGroupCheckedMap.value.add(selectedClassGroupCollectionId.value);
};

const handleUpdateSelectedRecipients = (newSelectedRecipients) => {
  const recipients = ObjectUtils.deepClone(selectedRecipients.value);
  let existingSelectedClassGroups: number[] = [];
  Object.keys(recipients).forEach((classGroupCollectionId) => {
    if (!isEmpty(recipients[parseInt(classGroupCollectionId)])) {
      existingSelectedClassGroups.push(parseInt(classGroupCollectionId));
    }
  });
  existingSelectedClassGroups = Array.from(new Set(existingSelectedClassGroups));
  const newSelectedTeachers = selected.value.map((teacher) => teacher.userId);
  initialSelected.value.forEach((recipient: string) => {
    if (!newSelectedTeachers.includes(recipient)) {
      existingSelectedClassGroups.forEach((classGroupCollectionId) => {
        selectedRecipients.value[classGroupCollectionId] = selectedRecipients.value[
          classGroupCollectionId
        ].filter((teacher) => teacher.userId !== recipient);
      });
    }
  });
  const existingTeachers = Object.values(selectedRecipients.value).flat(1);
  existingTeachers.forEach((user) => {
    if (!newSelectedTeachers.includes(user.userId)) {
      selectedRecipients.value[selectedClassGroupCollectionId.value] = selectedRecipients.value[
        selectedClassGroupCollectionId.value
      ].filter((teacher) => teacher.userId !== user.userId);
    }
  });
  newSelectedRecipients.forEach((teacher) => {
    if (isEmpty(selectedRecipients.value[selectedClassGroupCollectionId.value])) {
      selectedRecipients.value[selectedClassGroupCollectionId.value] = [];
    } else {
      const teacherInSelectedList = selectedRecipients.value[
        selectedClassGroupCollectionId.value
      ].find((obj) => obj.userId === teacher.userId);
      if (!isEmpty(teacherInSelectedList)) return; // teacher already exist in current list hence no action required
    }

    // selected teacher in another group
    if (teacher.studentGroupCollectionId !== selectedClassGroupCollectionId.value) {
      if (isDefined(selectedRecipients.value[teacher.studentGroupCollectionId])) {
        const indexInList = selectedRecipients.value[teacher.studentGroupCollectionId].findIndex(
          (recipient) => recipient.userId === teacher.userId,
        );
        if (indexInList === -1) {
          selectedRecipients.value[teacher.studentGroupCollectionId].push(teacher);
        }
      } else {
        selectedRecipients.value[teacher.studentGroupCollectionId] = [];
        selectedRecipients.value[teacher.studentGroupCollectionId].push(teacher);
      }
    } else {
      // find teacher data
      const selectedTeacher = teacherListByGroup.value[selectedClassGroupCollectionId.value].filter(
        (recipient) => recipient.userId === teacher.userId,
      );
      if (!isEmpty(selectedTeacher)) {
        selectedRecipients.value[selectedClassGroupCollectionId.value].push(teacher); // teacher data found and added into selected list
      }
    }
  });
};

const getTeacherData = (teacher, teacherListIndex: number) => {
  let classGroupUuid;
  let studentGroupCollectionId;
  if (teacherListIndex !== -1) {
    classGroupUuid = selectedClassGroupUuid.value;
    studentGroupCollectionId = selectedClassGroupCollectionId.value;
  } else {
    classGroupUuid = isDefined(teacher.classGroupUuid)
      ? teacher.classGroupUuid
      : lastSelectedClassGroupUuid.value;
    studentGroupCollectionId = isDefined(teacher.studentGroupCollectionId)
      ? teacher.studentGroupCollectionId
      : lastSelectedClassGroupCollectionId.value;
  }

  return {
    ...teacher,
    classGroupUuid,
    studentGroupCollectionId,
  };
};

const handleSelectedTeachersInClassGroups = () => {
  const selectedTeachers = [];
  const selectedTeachersId = [];

  selected.value.forEach((teacher) => {
    const teacherListIndex = teacherListByGroup.value[
      selectedClassGroupCollectionId.value
    ].findIndex((recipient) => recipient.userId === teacher.userId);

    const teacherData = getTeacherData(teacher, teacherListIndex);

    selectedTeachersId.push(teacher.userId);
    selectedTeachers.push(teacherData);
  });

  selectedAndNewlySelected.value.forEach((teacher) => {
    if (!selectedTeachersId.includes(teacher.userId)) {
      const teacherListIndex = teacherListByGroup.value[
        selectedClassGroupCollectionId.value
      ].findIndex((recipient) => recipient.userId === teacher.userId);

      const teacherData = getTeacherData(teacher, teacherListIndex);

      if (teacherListIndex !== -1) {
        selectedTeachers.push(teacherData);
      }
      selectedInner.value.push(teacherData.userId);
    }
  });

  if (!isEmpty(selectedClassGroupUuid.value)) {
    handleUpdateSelectedRecipients(selectedTeachers);
  }
};

const handleSearch = (search: { keyword: string; filter: Record<string, unknown> }) => {
  teacherName.value = search.keyword;
  appliedSearchFilters.value = search.filter;

  searchTeachers();

  closeFilterContentPopup();
};

const searchTeachers = () => {
  let filteredTeachers = teacherListByGroup.value[selectedClassGroupCollectionId.value] ?? [];
  if (isDefined(appliedSearchFilters.value)) {
    const roleFilter = appliedSearchFilters.value.role;
    if (isDefined(roleFilter) && roleFilter.length !== 0) {
      filteredTeachers = filteredTeachers.filter((teacher) => {
        if (!isEmpty(teacher.roleInGroup)) {
          return roleFilter.includes(teacher.roleInGroup);
        }
      });
    }

    const schoolFilter = appliedSearchFilters.value.school;
    if (isDefined(schoolFilter) && schoolFilter.length !== 0) {
      filteredTeachers = filteredTeachers.filter((teacher) => {
        if (!isEmpty(teacher.schoolName)) {
          return schoolFilter.includes(teacher.schoolName);
        }
      });
    }
  }

  const teacherNameLocal = teacherName.value;
  if (isDefined(teacherNameLocal) && teacherNameLocal !== "") {
    filteredTeachers = filteredTeachers.filter((teacher) => {
      return teacher.name.toLowerCase().indexOf(teacherNameLocal.toLowerCase()) !== -1;
    });
  }
  filteredTeacherList.value = filteredTeachers;
};

const closeFilterContentPopup = () => {
  showFilterPopup.value = false;
};

const toggleFilter = () => {
  showFilterPopup.value = !showFilterPopup.value;
};

const removeFilters = (filterChanged?) => {
  appliedSearchFilters.value = {
    role: [],
    school: [],
  };
  if (filterChanged) {
    filterChanged({});
  }
  searchTeachers();
};

const updateDirtyData = () => {
  if (selectedInner.value.length === 0) {
    removeDirty(dirtyId.value);
  } else {
    setDirty(dirtyId.value);
  }
};

const handleRemoveTag = (tag: { type: string }) => {
  if (tag.type === "school") {
    appliedSearchFilters.value = { ...appliedSearchFilters.value, school: [] };
  } else if (tag.type === "role") {
    appliedSearchFilters.value = { ...appliedSearchFilters.value, role: [] };
  }
  searchTeachers();
};

const toggleSettings = () => {
  showSettingsContentPopup.value = !showSettingsContentPopup.value;
};

const handleClearSelection = () => {
  selectedInner.value = [];
};

const handleAddTeacher = () => {
  selectedAndNewlySelected.value = [...selected.value];
  handleSelectedTeachersInClassGroups();

  emit("teachersAdded", selectedRecipients.value);

  removeDirty(dirtyId.value);

  actionBack();
};

// Lifecycle
onMounted(() => {
  initDataTableOptions();

  // From created
  if (hasParentSubpage) {
    parentSubpageName.value = titles.value.slice(-1)[0] ?? "";
  }
  titles.value.push(t("announcement_receiver_add_teachers_title"));
  initData();
});

defineExpose(defaultExpose);
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";

.add-teacher-from-group-content-subpage {
  .add-teacher-selection-listing {
    margin-top: $spacing-05;

    .search-bar-wrapper {
      padding: $spacing-03;
      background-color: $ui-01;
      border-radius: 10px 10px 0 0;

      .search-bar {
        justify-content: flex-end;

        > div {
          &:not(:first-child) {
            margin-left: $spacing-03;
          }
        }

        .bx--search-input {
          background-color: $ui-background;
          border-radius: 10px;
          &:hover {
            background-color: $hover-ui;
          }
        }

        .button {
          &.close {
            &:hover {
              border-radius: 0 10px 10px 0;
              background: inherit;
            }
          }
        }

        .filter-selection {
          border-radius: 100%;

          &.has-active-filters {
            border-radius: 10px;
          }
        }
      }
    }

    .filter-tag-list {
      background-color: $ui-01;
      padding: $spacing-03;
    }

    .data-table-list {
      .selection-bar {
        border-radius: 0;
      }

      > .v-table {
        > .v-table__wrapper {
          border-radius: 0;
        }
      }
    }
  }
}
</style>
